import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import DemoModal from './DemoModal';

const HeroSection = () => {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const navigate = useNavigate();

  return (
    <>
      <section className="relative min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-pink-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl"></div>
        
        <Navigation />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full">
                <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
                <span className="text-pink-700 font-semibold text-sm">24/7 AI Answering</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Stop Missing Calls, Losing Leads, and Leaving Money on the Table
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Imagine never missing another customer call — even when you're busy, closed, or unavailable. ReceptionGenie answers every call, books appointments, captures leads, and follows up automatically.
              </p>
              
              {/* Stats Badges */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div className="text-3xl font-bold text-pink-600">86%+</div>
                  <div className="text-sm text-gray-600 mt-1">Response Rate</div>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                  <div className="text-3xl font-bold text-pink-600">24/7</div>
                  <div className="text-sm text-gray-600 mt-1">Always Available</div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button 
                  onClick={() => navigate('/payment')}
                  className="whitespace-nowrap px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  Sign Up Now
                </button>
                <a 
                  href="tel:+18886009252"
                  className="whitespace-nowrap px-8 py-4 bg-white hover:bg-gray-50 text-pink-600 font-semibold rounded-lg border-2 border-pink-500 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer text-center"
                >
                  Call the AI Demo
                </a>
              </div>
            </div>
            
            {/* Right Content - Genie Mascot */}
            <div className="relative flex flex-col items-center">
              {/* Genie Image with padding */}
              <div className="relative z-10 mb-16">
                <img 
                  src="https://static.readdy.ai/image/f188085399ab07ac2c79984c802b33af/d7137dec17d438e55dac1175e416585f.png" 
                  alt="ReceptionGenie AI Mascot" 
                  className="w-full max-w-lg mx-auto drop-shadow-2xl"
                />
              </div>
              
              {/* Floating elements - positioned below the mascot with wider spacing and vertical offset */}
              <div className="relative w-full max-w-2xl mx-auto px-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 md:gap-12">
                  {/* Call Answered Card - positioned slightly higher */}
                  <div className="bg-white p-4 rounded-xl shadow-lg animate-bounce z-20 md:mt-0" style={{ animationDuration: '3s' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="ri-phone-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Call Answered</div>
                        <div className="text-xs text-gray-500">In 2 seconds</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Appointment Booked Card - positioned slightly lower */}
                  <div className="bg-white p-4 rounded-xl shadow-lg animate-bounce z-20 md:mt-8" style={{ animationDuration: '4s', animationDelay: '1s' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-calendar-check-line text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Appointment Booked</div>
                        <div className="text-xs text-gray-500">Automatically</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {showDemoModal && <DemoModal onClose={() => setShowDemoModal(false)} />}
    </>
  );
};

export default HeroSection;
